import { useMemo, useRef, memo } from "react";
import Loading from "./Loading";

const TableBody = memo(function TableBody({
  columns,
  data,
  emptyMessage,
  rowKey,
  rowClassName,
  rowStyle,
  cellClassName,
  isLoading,
}) {
  const getRowClassName = (row, index) => {
    if (typeof rowClassName === "function") return rowClassName(row, index);
    return "hover:bg-palette-indigo-soft transition-colors";
  };

  const getRowStyle = (row, index) => {
    if (typeof rowStyle === "function") return rowStyle(row, index);
    return {};
  };

  return (
    <tbody className="bg-white divide-y divide-palette-gray-divider">
      {isLoading ? (
        <tr>
          <td colSpan={columns.length} className="py-12">
            <div className="flex items-center justify-center">
              <Loading size="md" text="Loading data..." />
            </div>
          </td>
        </tr>
      ) : data.length === 0 ? (
        <tr>
          <td colSpan={columns.length} className="py-8 text-center text-palette-gray-secondary">
            {emptyMessage}
          </td>
        </tr>
      ) : (
        data.map((row, rIdx) => (
          <tr
            key={typeof rowKey === "function" ? rowKey(row, rIdx) : rowKey ? row[rowKey] : rIdx}
            className={getRowClassName(row, rIdx)}
            style={getRowStyle(row, rIdx)}
          >
            {columns.map((col, cIdx) => {
              let tdClassName = `px-3 py-2 text-sm ${cellClassName}`;
              if (col.cellClassName) tdClassName += ` ${col.cellClassName}`;

              return (
                <td
                  key={col.key || cIdx}
                  className={tdClassName}
                  style={{
                    textAlign: col.align || "left",
                    whiteSpace: col.nowrap ? "nowrap" : "normal",
                  }}
                >
                  {col.render ? col.render(row, rIdx) : row?.[col.key]}
                </td>
              );
            })}
          </tr>
        ))
      )}
    </tbody>
  );
});

function useStableBodyColumns(columns) {
  const cacheRef = useRef({ source: [], stable: [] });

  return useMemo(() => {
    const previous = cacheRef.current.source;
    const canReuse =
      previous.length === columns.length &&
      previous.every((prev, index) => {
        const next = columns[index];
        return (
          prev?.key === next?.key &&
          prev?.render === next?.render &&
          prev?.width === next?.width &&
          prev?.align === next?.align &&
          prev?.cellClassName === next?.cellClassName &&
          prev?.nowrap === next?.nowrap
        );
      });

    if (canReuse) return cacheRef.current.stable;

    const stable = columns.map((col) => ({
      key: col.key,
      render: col.render,
      width: col.width,
      align: col.align,
      cellClassName: col.cellClassName,
      nowrap: col.nowrap,
    }));

    cacheRef.current = { source: columns, stable };
    return stable;
  }, [columns]);
}

export default function DataTable({
  columns = [],
  data = [],
  emptyMessage = "No data available",
  rowKey,
  rowClassName,
  rowStyle,
  cellClassName = "font-semibold text-palette-slate-primary",
  isLoading = false,
  wrapperClassName,
  /**
   * Optional renderer for an extra <tr> placed above the header row,
   * inside the same <thead> (so column widths stay perfectly aligned).
   * Receives the column object and its index; return the cell content
   * (or null to leave the cell empty). Return `null` from the function
   * itself (not per-cell) to skip the row entirely.
   */
  renderPreHeaderCell,
  preHeaderRowClassName = "bg-palette-gray-soft",
  preHeaderCellClassName = "px-3 pt-2 pb-1 align-bottom",
}) {
  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeData = Array.isArray(data) ? data : [];
  const bodyColumns = useStableBodyColumns(safeColumns);

  return (
    <div className={`overflow-auto ${wrapperClassName ?? ""}`}>
      <table className="min-w-full divide-y divide-palette-gray-divider">
        <thead className="bg-palette-gray-soft">
          {renderPreHeaderCell && (
            <tr className={preHeaderRowClassName}>
              {safeColumns.map((column, index) => (
                <td
                  key={`prehdr-${column.key || index}`}
                  className={preHeaderCellClassName}
                  style={{
                    textAlign: column.align || "left",
                    width: column.width || undefined,
                  }}
                >
                  {renderPreHeaderCell(column, index)}
                </td>
              ))}
            </tr>
          )}
          <tr>
            {safeColumns.map((column, index) => (
              <th
                key={column.key || `header-${index}`}
                className={`px-3 py-2 text-xs font-semibold text-palette-gray-heading uppercase tracking-wider border-b-2 border-palette-gray-divider ${column.headerClassName || ""}`}
                style={{
                  textAlign: column.align || "left",
                  width: column.width || undefined,
                }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>

        <TableBody
          columns={bodyColumns}
          data={safeData}
          emptyMessage={emptyMessage}
          rowKey={rowKey}
          rowClassName={rowClassName}
          rowStyle={rowStyle}
          cellClassName={cellClassName}
          isLoading={isLoading}
        />
      </table>
    </div>
  );
}
